// Resolves which Photohawk API domain to use based on the current frontend host.
// If the app is served from photohawk-staging.com (or any subdomain of it),
// the staging API domain is used; otherwise the production domain is used.

import { getPhotohawkEnv } from "@/lib/env";

export const getApiBaseDomain = (): string =>
  getPhotohawkEnv() === "staging" ? "photohawk-staging.com" : "photohawk.com";

export const ADMIN_API_ENDPOINT = `https://admin.api.${getApiBaseDomain()}`;
export const SEARCH_API_ENDPOINT = `https://search.api.${getApiBaseDomain()}`;
export const AUTH_API_ENDPOINT = `https://auth.api.${getApiBaseDomain()}`;
// Studio stack (split out of api-admin to stay under the CloudFormation resource
// limit): uploader directory/presign + the super-admin video re-scan endpoints.
export const STUDIO_API_ENDPOINT = `https://studio.api.${getApiBaseDomain()}`;
export const WORKSPACES_API_ENDPOINT = `https://workspaces.api.${getApiBaseDomain()}`;
export const CHECKOUT_API_ENDPOINT = `https://checkout.api.${getApiBaseDomain()}`;
// Billing (usage estimate, invoices, plan switch, payment methods) lives on its
// own serverless stack + domain (api-admin-billing → billing.api.<domain>).
export const BILLING_API_ENDPOINT = `https://billing.api.${getApiBaseDomain()}`;
// Internal platform admin (overview, workspace directory, email campaigns) lives
// on its own serverless stack + domain (api-platform → platform.api.<domain>),
// split out of api-admin to stay under the CloudFormation 500-resource limit.
export const PLATFORM_API_ENDPOINT = `https://platform.api.${getApiBaseDomain()}`;
// On-demand per-workspace FTP servers (photohawk-serverless ftp-ondemand stack).
export const FTPSERVER_API_ENDPOINT = `https://ftpserver.api.${getApiBaseDomain()}`;
// Face services (photohawk-serverless api-faces stack): face detect / crop, and the staff-only People groups.
export const FACES_API_ENDPOINT = `https://faces.api.${getApiBaseDomain()}`;
// Staff-only Support knowledge base (photohawk-serverless api-support-kb stack; platform-admin gated). Articles come
// only from this authenticated API — never from the bundle.
export const SUPPORT_KB_API_ENDPOINT = `https://kb.api.${getApiBaseDomain()}`;
// Gallery analytics capture + the live "visitors in the last 30 min" count (api-analytics stack).
export const ANALYTICS_API_ENDPOINT = `https://analytics.api.${getApiBaseDomain()}`;

/**
 * The host the photohawk-serverless backend uses to identify a tenant. The
 * legacy apps run on `{alias}.photohawk.com`, so the tenant is implied by the
 * subdomain. From photohawk-web (a single host) we pass it explicitly via a
 * `hostname` param — derived here from the workspace alias / custom domain.
 */
export const tenantHostname = (opts: {
  alias?: string | null;
  customDomain?: string | null;
}): string | undefined => {
  if (opts.customDomain) return opts.customDomain;
  if (opts.alias) return `${opts.alias}.${getApiBaseDomain()}`;
  return undefined;
};
